/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { useServiceContext } from '../../../../root'
import ModalConfirmation from '../../../../components/shared/ModalConfirmation'
import HtmlCell from '../../../../components/shared/HtmlCell'
import { toast } from '../../../../services/toast'
import { getPropertyUsageMetadata, Metadata, remove } from '../../../../services/api/metadata'

interface ModalRemoveProps {
  property: Metadata
  onClose: () => void
  onSuccess?: () => void
}

const ModalRemove: React.FC<ModalRemoveProps> = ({ property, onClose, onSuccess }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter

  const [msg, setMsg] = useState<React.ReactNode>('')
  const [loading, setLoading] = useState<Promise<any> | null>(null)

  useEffect(() => {
    selectMessage()
  }, [])

  const selectMessage = async () => {
    try {
      const promise = getPropertyUsageMetadata(property.id)
      setLoading(promise)

      const data: any = await promise
      const usedDocsIds = data.documentTypeTemplateIds
      const usedFoldIds = data.folderTypeTemplateIds
      const usedFsiIds = data.fsiIds
      const usedWatermarkIds = data.watermarkIds
      const fsitypes: string[] = []
      const watermartypes: string[] = []
      const doctypes: string[] = []
      const foldertypes: string[] = []

      if (data.documentTypeTemplateIds !== null) {
        for (let i = 0; i < usedDocsIds.length; i++) {
          doctypes.push(data.documentTypeTemplateIds[i].name)
        }
      }

      if (data.folderTypeTemplateIds !== null) {
        for (let j = 0; j < usedFoldIds.length; j++) {
          foldertypes.push(data.folderTypeTemplateIds[j].name)
        }
      }

      if (data.watermarkIds !== null) {
        for (let d = 0; d < usedWatermarkIds.length; d++) {
          watermartypes.push(data.watermarkIds[d].name)
        }
      }

      if (data.fsiIds !== null) {
        for (let k = 0; k < usedFsiIds.length; k++) {
          fsitypes.push(data.fsiIds[k].name)
        }
      }

      const textMsg = (
        <div>
          <HtmlCell>{translate('SETTINGS_METADATA_DELETE', { metadataName: property.name })}</HtmlCell>
          {usedDocsIds !== null && (
            <HtmlCell>{translate('SETTINGS_METADATA_DELETE_DOCUMENT', { doctypes: doctypes.join(', ') })}</HtmlCell>
          )}

          {usedFoldIds !== null && (
            <HtmlCell>{translate('SETTINGS_METADATA_DELETE_FOLDER', { foldertype: foldertypes.join(', ') })}</HtmlCell>
          )}

          {usedWatermarkIds !== null && (
            <HtmlCell>
              {translate('SETTINGS_METADATA_DELETE_WATERMARK', { watermarktype: watermartypes.join(', ') })}
            </HtmlCell>
          )}

          {usedDocsIds === null && usedFoldIds === null && usedWatermarkIds === null && usedFsiIds !== null && (
            <HtmlCell>{translate('SETTINGS_METADATA_DELETE_FSI', { fsitype: fsitypes.join(', ') })}</HtmlCell>
          )}
        </div>
      )

      setMsg(textMsg)
      setLoading(null)
    } catch (error) {
      console.error('Error loading property usage:', error)
      setLoading(null)
    }
  }

  const doSave = async () => {
    try {
      await remove(property.id)
      toast.success(translate('METADATA_DELETE_SUCCESS_MSG'))

      if (onSuccess) {
        onSuccess()
      }
      onClose()
    } catch (error: any) {
      const errorMsg = error?.data?.message || error?.message || 'Unknown error'
      toast.error(translate('METADATA_DELETE_FAIL_MSG') + ': ' + errorMsg)
    }
  }

  return (
    <ModalConfirmation
      titleMsg={translate('SETTINGS_METADATA_DELETE_TITLE')}
      loading={loading}
      msg={msg}
      cancelTxt={translate('CANCEL')}
      okTxt={translate('OK')}
      onSubmit={doSave}
      onCancel={onClose}
    />
  )
}

export default ModalRemove
